/**
 * Fetch API 기반 HTTP 클라이언트
 *
 * 책임:
 * - 모든 요청에 localStorage의 token을 Authorization 헤더로 자동 주입
 * - 401 응답 감지 → AuthContext 초기화 (자동 로그아웃)
 * - 모든 HTTP 에러를 통일된 { message: string } 형태로 변환
 * - Base URL: 환경변수 REACT_APP_API_BASE (없으면 같은 origin의 /api)
 */

import type { ApiError } from './types';

// 백엔드 host: 개발 시엔 .env.development의 REACT_APP_API_BASE(localhost:8080)를,
// 운영/미설정 시엔 같은 origin의 /api를 사용
const API_BASE = process.env.REACT_APP_API_BASE ?? '/api';

/**
 * localStorage에서 토큰을 읽어온다.
 * 토큰이 없으면 에러를 던진다. (useAuth 훅에서 guard)
 */
function getToken(): string {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No auth token found');
  }
  return token;
}

/**
 * HTTP 헤더를 구성한다.
 * 토큰이 제공되면 Authorization: Bearer {token} 추가
 */
function buildHeaders(token?: string): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Fetch 응답을 처리한다.
 * - 401 (인증된 요청): 토큰 만료 → 로그아웃 처리 + CustomEvent 발행
 * - 401 (로그인 등 미인증 요청): 자격증명 오류로 취급 → 일반 에러 메시지
 * - 4xx/5xx: 에러 메시지 추출 및 예외 던짐
 * - 2xx: JSON 파싱 후 반환
 *
 * @param authenticated 토큰을 실어 보낸 요청인지 여부.
 *   true일 때의 401만 "세션 만료"로 간주하고 자동 로그아웃을 트리거한다.
 *   로그인 요청(false)의 401은 잘못된 아이디/비밀번호이므로 세션 만료가 아니다.
 */
async function handleResponse<T>(res: Response, authenticated: boolean): Promise<T> {
  // 401 Unauthorized: 인증된 요청이면 토큰 만료로 간주
  if (res.status === 401 && authenticated) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // AuthContext에서 listen할 커스텀 이벤트 발행
    window.dispatchEvent(new CustomEvent('auth:logout'));
    throw new Error('세션이 만료되었습니다. 다시 로그인해 주세요.');
  }

  // HTTP 에러 응답 (미인증 401 포함, 4xx/5xx)
  if (!res.ok) {
    try {
      const error = (await res.json()) as ApiError;
      throw new Error(error.message || defaultErrorMessage(res.status));
    } catch (err) {
      // 위에서 던진 Error는 그대로 전파 (JSON 파싱 실패만 fallback 처리)
      if (err instanceof Error && err.message) {
        throw err;
      }
      throw new Error(defaultErrorMessage(res.status));
    }
  }

  // 성공 응답 (2xx)
  return res.json() as Promise<T>;
}

/** 백엔드가 메시지를 주지 않을 때 상태코드별 기본 문구. */
function defaultErrorMessage(status: number): string {
  if (status === 401) {
    return '아이디 또는 비밀번호가 올바르지 않습니다.';
  }
  return `HTTP ${status}`;
}

/**
 * GET 요청 (타입: T)
 * @param path API 경로 (/dashboard/kpi-summary 등)
 * @param useToken 토큰 사용 여부. false면 로그인 요청처럼 토큰 없이 전송
 */
export async function get<T>(path: string, useToken = true): Promise<T> {
  const token = useToken ? getToken() : undefined;
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'GET',
    headers: buildHeaders(token),
  });
  return handleResponse<T>(res, useToken);
}

/**
 * 바이너리 파일 다운로드 (GET)
 * JSON이 아닌 파일 응답(스냅샷 등)을 Blob으로 받고, Content-Disposition에서 파일명을 파싱한다.
 * @param path API 경로 (/{module}/snapshots/{id} 등)
 * @param fallbackName Content-Disposition이 없을 때 사용할 기본 파일명
 */
export async function getBlob(
  path: string,
  fallbackName: string
): Promise<{ blob: Blob; filename: string }> {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  // 인증된 요청의 401은 세션 만료 처리(다른 요청과 동일 동작).
  if (res.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.dispatchEvent(new CustomEvent('auth:logout'));
    throw new Error('세션이 만료되었습니다. 다시 로그인해 주세요.');
  }
  if (!res.ok) {
    throw new Error(defaultErrorMessage(res.status));
  }
  const blob = await res.blob();
  const filename = parseFilename(res.headers.get('Content-Disposition')) ?? fallbackName;
  return { blob, filename };
}

/** Content-Disposition 헤더에서 filename="..."을 추출한다. 없으면 null. */
function parseFilename(disposition: string | null): string | null {
  if (!disposition) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * POST 요청 (타입: T)
 * @param path API 경로 (/auth/login, /modules/{name}/run-now 등)
 * @param body 요청 본문 (선택)
 * @param useToken 토큰 사용 여부. false면 로그인 요청처럼 토큰 없이 전송
 */
export async function post<T>(
  path: string,
  body?: object,
  useToken = true
): Promise<T> {
  const token = useToken ? getToken() : undefined;
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: body ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(res, useToken);
}

/**
 * PATCH 요청 (타입: T)
 * 부분 업데이트 (모듈 설정 수정 등)
 * @param path API 경로 (/modules/{name} 등)
 * @param body 요청 본문
 */
export async function patch<T>(path: string, body: object): Promise<T> {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res, true);
}
